import { supabase } from "./supabase";
import type { ApiError } from "@colab/shared";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

type FetchOptions = RequestInit & {
  previewAs?: string | null;
};

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

// Module-level subscription set by PreviewAsProvider on mount. apiFetch
// consults it when the caller did NOT pass an explicit `previewAs` option.
// This lets every existing hook (use-dashboard, use-resources, use-mdf-list,
// etc.) automatically carry the X-Preview-As header during preview-as
// navigation without rewriting their call sites.
//
// Set to () => null when no preview is active. Tests assert this by entering
// preview-as, navigating, and verifying audit_log.preview_as rows accumulate.
let previewAsResolver: () => string | null = () => null;

export function setPreviewAsResolver(resolver: () => string | null): void {
  previewAsResolver = resolver;
}

// Typed fetch wrapper for the SPA → Hono worker boundary.
//
// Attaches the Supabase access token as Authorization (the worker auth
// middleware accepts either the cookie or the bearer header).
//
// If preview-as is active, attaches the X-Preview-As header. The worker
// re-verifies the partner UUID on every call and writes an audit log — never
// trust this header without server-side re-check.
//
// Priority for preview-as header: explicit `previewAs` option > module
// resolver. Explicit `null` opts the call OUT of header injection (used by
// /auth/preview-as activation itself — passing the header on the call that
// activates preview-as would be a chicken-and-egg loop).
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { previewAs, headers, ...rest } = options;
  const effectivePreviewAs =
    previewAs === null ? null : previewAs ?? previewAsResolver();

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const merged = new Headers(headers);
  merged.set("content-type", "application/json");
  if (session?.access_token) {
    merged.set("authorization", `Bearer ${session.access_token}`);
  }
  if (effectivePreviewAs) {
    merged.set("x-preview-as", effectivePreviewAs);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    credentials: "include",
    headers: merged,
  });

  if (!res.ok) {
    let payload: ApiError | null = null;
    try {
      payload = (await res.json()) as ApiError;
    } catch {
      // body was not JSON
    }
    throw new ApiClientError(
      res.status,
      payload?.error.code ?? "unknown_error",
      payload?.error.message ?? `Request failed: ${res.status}`,
      payload?.error.details,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
